/**
 * Escritor de ZIP no browser, sem dependência nova.
 *
 * O zip do material da arte é montado pela API (`/app-art/{id}/download`),
 * porque lá os arquivos estão no bucket e o browser não os lê. As placas
 * preenchidas são o caso oposto: elas só existem aqui, rasterizadas do DOM, e
 * subir dezenas de PNGs para a API só para ela devolver empacotado seria ida e
 * volta à toa.
 *
 * É o mesmo formato mínimo do `utils/zip.ts` do backend — cabeçalho local,
 * dado, diretório central e EOCD, sem ZIP64 — com uma diferença: aqui tudo é
 * ARMAZENADO (método 0). O conteúdo são PNGs, que já vêm comprimidos; deflate
 * neles gastaria CPU da aba para quase nada.
 */

const CRC_TABLE = (() => {
  const table = new Int32Array(256);
  for (let n = 0; n < 256; n++) {
    let c = n;
    for (let k = 0; k < 8; k++) {
      c = c & 1 ? 0xEDB88320 ^ (c >>> 1) : c >>> 1;
    }
    table[n] = c;
  }
  return table;
})();

const crc32 = (bytes: Uint8Array): number => {
  let crc = -1;
  for (let i = 0; i < bytes.length; i++) {
    crc = (crc >>> 8) ^ CRC_TABLE[(crc ^ bytes[i]) & 0xFF];
  }
  return (crc ^ -1) >>> 0;
};

/** Data/hora MS-DOS. Descompactador que lê zero aqui às vezes recusa a entrada. */
const dosDateTime = (date: Date) => ({
  time: ((date.getHours() << 11) | (date.getMinutes() << 5) | (date.getSeconds() / 2)) & 0xFFFF,
  date: (((date.getFullYear() - 1980) << 9) | ((date.getMonth() + 1) << 5) | date.getDate()) & 0xFFFF,
});

export interface ZipEntry {
  /** Caminho dentro do pacote. Barra normal, sem `../` e sem barra inicial. */
  name: string;
  /* `<ArrayBuffer>` e não o `Uint8Array` genérico: o genérico admite
     SharedArrayBuffer por baixo, e o `Blob` recusa. */
  content: Uint8Array<ArrayBuffer>;
}

export const createZip = (entries: ZipEntry[]): Blob => {
  const parts: BlobPart[] = [];
  const centrals: Uint8Array<ArrayBuffer>[] = [];
  const encoder = new TextEncoder();
  const { time, date } = dosDateTime(new Date());
  let offset = 0;

  for (const entry of entries) {
    const name = encoder.encode(entry.name);
    const data = entry.content;
    const crc = crc32(data);

    const local = new DataView(new ArrayBuffer(30));
    local.setUint32(0, 0x04034b50, true);
    local.setUint16(4, 20, true);          // versão mínima
    local.setUint16(6, 0x0800, true);      // bit 11: nome em UTF-8
    local.setUint16(8, 0, true);           // armazenado
    local.setUint16(10, time, true);
    local.setUint16(12, date, true);
    local.setUint32(14, crc, true);
    local.setUint32(18, data.length, true);
    local.setUint32(22, data.length, true);
    local.setUint16(26, name.length, true);
    parts.push(local.buffer, name, data);

    const central = new DataView(new ArrayBuffer(46));
    central.setUint32(0, 0x02014b50, true);
    central.setUint16(4, 20, true);        // versão de quem escreveu
    central.setUint16(6, 20, true);        // versão mínima
    central.setUint16(8, 0x0800, true);
    central.setUint16(10, 0, true);
    central.setUint16(12, time, true);
    central.setUint16(14, date, true);
    central.setUint32(16, crc, true);
    central.setUint32(20, data.length, true);
    central.setUint32(24, data.length, true);
    central.setUint16(28, name.length, true);
    central.setUint32(42, offset, true);
    centrals.push(new Uint8Array(central.buffer), name);

    offset += 30 + name.length + data.length;
  }

  const centralSize = centrals.reduce((sum, part) => sum + part.length, 0);
  const end = new DataView(new ArrayBuffer(22));
  end.setUint32(0, 0x06054b50, true);
  end.setUint16(8, entries.length, true);
  end.setUint16(10, entries.length, true);
  end.setUint32(12, centralSize, true);
  end.setUint32(16, offset, true);

  return new Blob([...parts, ...centrals, end.buffer], { type: 'application/zip' });
};
